<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { logout } from "@/logout";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { useCompanyFeatureStore } from "@/stores/companyFeatureStore";
import nerdforceLogo from "@/assets/branding/nerdforce/logo-nerdforce-running-sloth.png";

const userStore = useUserStore();
const entitlementStore = useEntitlementStore();
const companyFeatureStore = useCompanyFeatureStore();
const featureEnabled = (featureId) => process.env.VUE_APP_DISABLE_AUTH === "true" || companyFeatureStore.isEnabled(featureId);
watch(() => userStore.currentCompanyId, () => {
  companyFeatureStore.loadFeatures().catch(() => companyFeatureStore.clear());
}, { immediate: true });
const canUseInvoiceWorkspace = computed(() =>
  entitlementStore.hasAllModules(["invoicing", "e-invoice"])
);
const isAdmin = computed(() =>
  userStore.isSystemAdmin ||
  (
    entitlementStore.hasModule("invoicing") &&
    ["companies.read", "customers.read", "articles.read", "files.read"]
      .some((permission) => userStore.hasPermission(permission))
  ) ||
  [
    "admin.users.read",
    "admin.groups.read",
    "admin.roles.read",
    "admin.identityproviders.read",
    "provisioning.requests.read",
  ].some((permission) => userStore.hasPermission(permission))
);
const canReadCompanies = computed(() =>
  entitlementStore.hasModule("invoicing") && userStore.hasPermission("companies.read")
);
const canReadCustomers = computed(() =>
  entitlementStore.hasModule("invoicing") && userStore.hasPermission("customers.read")
);
const canReadArticles = computed(() =>
  entitlementStore.hasModule("invoicing") && userStore.hasPermission("articles.read")
);
const canReadTemplates = computed(() =>
  entitlementStore.hasModule("invoicing") && userStore.hasPermission("files.read")
);
const canReadAdminUsers = computed(() => userStore.hasPermission("admin.users.read"));
const canReadAdminGroups = computed(() => userStore.hasPermission("admin.groups.read"));
const canReadAdminRoles = computed(() => userStore.hasPermission("admin.roles.read"));
const canReadIdentityProviders = computed(() => userStore.hasPermission("admin.identityproviders.read"));
const canReadProvisioning = computed(() => userStore.hasPermission("provisioning.requests.read"));
const canReadHandbook = computed(() => userStore.hasPermission("user.profile.read"));
const canReadBankingOpos = computed(() =>
  featureEnabled("banking-opos") && (
    (entitlementStore.hasModule("banking") && userStore.hasPermission("banking.read")) ||
    (entitlementStore.hasModule("opos") && userStore.hasPermission("opos.read"))
  )
);
const canReadBilling = computed(() =>
  featureEnabled("billing") && entitlementStore.hasAllModules(["invoicing", "kimai-integration"])
    && userStore.hasPermission("billing.read")
);
const workforcePermissions = [
  "workforce.self.read",
  "workforce.time.write",
  "workforce.leave.request",
  "workforce.team.read",
  "workforce.leave.approve",
  "workforce.hr.manage",
  "workforce.audit.read",
  "workforce.import",
];
const entitlementStatus = computed(() => {
  if (entitlementStore.loadFailed) {
    return { className: "text-bg-danger", label: "Lizenzstatus fehlt", icon: "bi-shield-x" };
  }
  if (entitlementStore.isOffline) {
    return { className: "text-bg-warning", label: "Offline", icon: "bi-cloud-slash" };
  }
  if (entitlementStore.isDenied) {
    return { className: "text-bg-danger", label: "Gesperrt", icon: "bi-shield-lock" };
  }
  return null;
});
const canUseWorkforce = computed(() =>
  featureEnabled("workforce") && entitlementStore.hasAnyModules(["workforce-time", "leave"])
    && workforcePermissions.some((permission) => userStore.hasPermission(permission))
);
const handbookLabel = computed(() => {
  const locale = String(userStore.locale || localStorage.getItem("mkzugferd.locale") || "de").toLowerCase();
  return locale.startsWith("de") ? "Handbuch" : "Handbook";
});
const apiUsageBadge = computed(() => {
  const usage = userStore.apiUsage;
  if (!usage?.enabled) {
    return null;
  }

  switch (usage.currentMode) {
    case "free":
      return {
        className: "text-bg-success",
        label: `${usage.includedCreditsRemaining} Credits frei`,
      };
    case "paid":
      return {
        className: "text-bg-warning",
        label: "kostenpflichtig",
      };
    case "blocked":
      return {
        className: "text-bg-danger",
        label: "blockiert",
      };
    default:
      return null;
  }
});
const navbarLogoSrc = computed(() => {
  if (userStore.logoObjectUrl) {
    return userStore.logoObjectUrl;
  }

  // Built-in fallback branding for Nerdforce companies.
  const company = userStore.currentCompany;
  const companyName = String(company?.name || "").toLowerCase();
  const companyNumber = String(company?.companyNumber || "").toLowerCase();
  const effectiveTheme = String(userStore.theme || company?.defaultTheme || "").trim().toLowerCase();

  if (effectiveTheme === "nerdforce" || companyName.includes("nerdforce") || companyNumber.includes("nerdforce")) {
    return nerdforceLogo;
  }

  return null;
});

// Bootstrap's Dropdown data API is flaky in this app context (menu never opens).
// We toggle the menu explicitly to keep the navbar usable and testable.
const isAdminMenuOpen = ref(false);
const adminDropdownRef = ref(null);
const route = useRoute();

function toggleAdminMenu() {
  isAdminMenuOpen.value = !isAdminMenuOpen.value;
}

function closeAdminMenu() {
  isAdminMenuOpen.value = false;
}

function onDocumentClick(e) {
  if (!isAdminMenuOpen.value) return;
  const el = adminDropdownRef.value;
  if (!el) return;
  if (el.contains(e.target)) return;
  isAdminMenuOpen.value = false;
}

watch(
  () => route.fullPath,
  () => {
    isAdminMenuOpen.value = false;
  }
);

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});
</script>

<template>
  <nav
    class="navbar navbar-expand-lg navbar-light"
    :style="{
      backgroundColor: 'var(--app-navbar-bg)',
      borderBottom: '1px solid var(--app-navbar-border)',
    }"
  >
    <div class="container py-2">
      <div class="d-flex w-100 align-items-center gap-3">
        <img
          v-if="navbarLogoSrc"
          :src="navbarLogoSrc"
          alt="Company logo"
          style="height: 42px; width: auto; object-fit: contain"
        />
        <router-link
          class="navbar-brand m-0"
          :to="{ name: canUseInvoiceWorkspace ? 'Invoices' : 'UserSettings' }"
          :style="{ color: 'var(--app-navbar-text)' }"
        >
          E-Rechnungen
        </router-link>

        <div class="small text-muted d-none d-lg-block" v-if="userStore.currentCompany">
          <span class="me-1">Firma:</span>
          <strong>{{ userStore.currentCompany.name }}</strong>
          <span v-if="apiUsageBadge" class="badge ms-2" :class="apiUsageBadge.className">
            API {{ apiUsageBadge.label }}
          </span>
        </div>

        <button
          class="navbar-toggler ms-auto"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#appNavbarCollapse"
          aria-controls="appNavbarCollapse"
          aria-expanded="false"
          aria-label="Toggle navigation"
          data-testid="navbar-toggler"
        >
          <span class="navbar-toggler-icon"></span>
        </button>
      </div>

      <div class="small text-muted mt-2 d-lg-none" v-if="userStore.currentCompany">
        <span class="me-1">Firma:</span>
        <strong>{{ userStore.currentCompany.name }}</strong>
        <span v-if="apiUsageBadge" class="badge ms-2" :class="apiUsageBadge.className">
          API {{ apiUsageBadge.label }}
        </span>
      </div>

      <div
        class="collapse navbar-collapse mt-3 mt-lg-0"
        id="appNavbarCollapse"
        data-testid="navbar-collapse"
      >
        <ul class="navbar-nav me-auto">
          <li class="nav-item" v-if="canUseInvoiceWorkspace">
            <router-link
              class="nav-link"
              :style="{ color: 'var(--app-navbar-link)' }"
              :to="{ name: 'Invoices' }"
            >
              Rechnungen
            </router-link>
          </li>
          <li class="nav-item" v-if="canReadBankingOpos">
            <router-link
              class="nav-link"
              :style="{ color: 'var(--app-navbar-link)' }"
              :to="{ name: 'BankingOpos' }"
            >
              Bank &amp; OPOS
            </router-link>
          </li>
          <li class="nav-item" v-if="canUseWorkforce">
            <router-link
              class="nav-link"
              :style="{ color: 'var(--app-navbar-link)' }"
              :to="{ name: 'Workforce' }"
            >
              Arbeitszeit
            </router-link>
          </li>
          <li class="nav-item" v-if="canReadBilling">
            <router-link
              class="nav-link"
              :style="{ color: 'var(--app-navbar-link)' }"
              :to="{ name: 'BillingAutomation' }"
            >
              Kimai-Abrechnung
            </router-link>
          </li>
          <li class="nav-item" v-if="canReadHandbook">
            <router-link
              class="nav-link"
              :style="{ color: 'var(--app-navbar-link)' }"
              :to="{ name: 'Handbook' }"
            >
              {{ handbookLabel }}
            </router-link>
          </li>

          <li
            class="nav-item dropdown"
            v-if="isAdmin"
            :class="{ show: isAdminMenuOpen }"
            ref="adminDropdownRef"
          >
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              :aria-expanded="isAdminMenuOpen ? 'true' : 'false'"
              :style="{ color: 'var(--app-navbar-link)' }"
              data-testid="navbar-admin-toggle"
              :class="{ show: isAdminMenuOpen }"
              @click.prevent="toggleAdminMenu"
            >
              Administration
            </a>
            <ul
              class="dropdown-menu"
              data-testid="navbar-admin-menu"
              :class="{ show: isAdminMenuOpen }"
              @click="closeAdminMenu"
            >
              <li v-if="canReadCompanies">
                <router-link class="dropdown-item" :to="{ name: 'CompanyAdmin' }">Firmen</router-link>
              </li>
              <li v-if="canReadCustomers">
                <router-link class="dropdown-item" :to="{ name: 'CustomersAdmin' }">Kunden</router-link>
              </li>
              <li v-if="canReadArticles">
                <router-link class="dropdown-item" :to="{ name: 'ArticlesAdmin' }">Artikel</router-link>
              </li>
              <li v-if="canReadTemplates">
                <router-link class="dropdown-item" :to="{ name: 'TemplateAdmin' }">Vorlagen</router-link>
              </li>
              <li v-if="canReadProvisioning">
                <router-link class="dropdown-item" :to="{ name: 'ProvisioningAdmin' }">Provisionierung</router-link>
              </li>
              <li v-if="canReadAdminUsers || canReadAdminGroups || canReadAdminRoles || canReadIdentityProviders"><hr class="dropdown-divider" /></li>
              <li v-if="canReadAdminUsers">
                <router-link class="dropdown-item" :to="{ name: 'AdminUsers' }">Benutzer</router-link>
              </li>
              <li v-if="canReadAdminGroups">
                <router-link class="dropdown-item" :to="{ name: 'AdminGroups' }">Gruppen</router-link>
              </li>
              <li v-if="canReadAdminRoles">
                <router-link class="dropdown-item" :to="{ name: 'AdminRoles' }">Rollen</router-link>
              </li>
              <li v-if="canReadIdentityProviders">
                <router-link class="dropdown-item" :to="{ name: 'AdminIdentityProviders' }"
                  >Identity Provider</router-link
                >
              </li>
            </ul>
          </li>
        </ul>

        <div class="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 mt-3 mt-lg-0">
          <span
            v-if="entitlementStatus"
            class="badge d-inline-flex align-items-center gap-1"
            :class="entitlementStatus.className"
            data-testid="entitlement-status"
          >
            <i class="bi" :class="entitlementStatus.icon" aria-hidden="true"></i>
            {{ entitlementStatus.label }}
          </span>
          <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'UserSettings' }">
            Einstellungen
          </router-link>
          <button type="button" class="btn btn-danger btn-sm" @click="logout">Logout</button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: "AppNavbar",
};
</script>
